/* eslint no-shadow: ["error", { "allow": ["state"] }] */

import axios from 'axios';
import config from '../../config';
import routes from '../../router/rails-routes';
import { findTaxon } from '../../models/taxon';

let timeoutId = null;

function propertyParams(properties, scopeName) {
  const propertyPs = {};
  properties.forEach((p) => {
    const values = [].concat(p.selectedValues);
    if (values.length > 0) { propertyPs[`${scopeName || p.name}_${p.id}`] = values; }
  });
  return propertyPs;
}

function optionParams(options, scopeName) {
  const optionPs = {};
  options.forEach((o) => {
    const values = [].concat(o.selectedValues);
    if (values.length > 0) { optionPs[`${scopeName || o.name}_${o.id}`] = values.map((v) => v.id); }
  });
  return optionPs;
}

function selectedTaxonIdParam(taxon) {
  return (taxon && taxon.id !== 0) ? taxon.id : null;
}

function addSelectedPropertyValues(properties, values) {
  return properties.map((p) => {
    const property = p;
    p.selectedValues = [];
    values.forEach((value) => {
      if (property.id === value.id && value.value !== '') {
        property.selectedValues.push(value.value);
      }
    });
    return property;
  });
}

function addSelectedOptionValues(options, values) {
  return options.map((o) => {
    const option = o;
    option.selectedValues = [];
    values.forEach((value) => {
      if (option.id === value.optionId && value.value !== '') {
        const safeValue = option.values.find((realValue) => realValue.id === value.value) || { id: value.value, name: '...' };
        option.selectedValues = [...option.selectedValues, safeValue];
      }
    });
    return option;
  });
}

const state = {
  locale: 'fr',
  propertiesFilters: null,
  optionsFilters: null,
  query: '',
  products: null,
  taxons: null,
  selectedTaxon: null,
  properties: [],
  filterStatuses: [],
  options: [],
  supplier: null,
  pagination: {
    current: 1,
    per_page: 25,
    pages: [],
    total_count: null,
    total_pages: null,
    next: null,
    prev: null,
    asked: 1,
  },
};

const mutations = {
  UPDATE_LOCALE(state, payload) {
    state.locale = payload;
  },

  UPDATE_PROPERTIES_FILTERS(state, payload) {
    state.propertiesFilters = payload;
  },
  UPDATE_OPTIONS_FILTERS(state, payload) {
    state.optionsFilters = payload;
  },
  UPDATE_TAXONS(state, payload) {
    state.taxons = payload;
  },
  UPDATE_SELECTED_TAXON(state, payload) {
    state.selectedTaxon = payload;
  },
  UPDATE_CRITERIA(state, payload) {
    state.taxon = payload.taxon;
    state.query = payload.query;
    const p = { current: payload.page };
    state.pagination = { ...state.pagination, ...p };

    let newPropertyValues = [];
    let newOptionValues = [];

    Object.keys(payload).forEach((name) => {
      if (name.startsWith('p_')) {
        const propertyId = parseInt(name.replace('p_', ''), 10);
        newPropertyValues = [
          ...newPropertyValues,
          ...[...new Set(payload[name].split(','))].map((value) => ({ id: propertyId, value })),
        ];
      }
      if (name.startsWith('o_')) {
        const optionId = parseInt(name.replace('o_', ''), 10);
        newOptionValues = [
          ...newOptionValues,
          ...[...new Set(payload[name].split(','))].map((value) => ({ optionId, value: parseInt(value, 10) })),
        ];
      }
    });

    state.properties = addSelectedPropertyValues(state.properties, newPropertyValues);
    state.options = addSelectedOptionValues(state.options, newOptionValues);
  },
  UPDATE_QUERY(state, payload) {
    state.query = payload;
  },
  UPDATE_PRODUCTS(state, payload) {
    state.products = payload;
  },
  UPDATE_PRODUCT(state, payload) {
    state.products = state.products.map((p) => {
      const prod = p;
      if (prod.id === payload.id) {
        prod.description = payload.description;
        prod.synonyms = payload.synonyms;
        prod.file_msds = payload.file_msds;
        prod.properties = payload.properties;
        prod.variants = payload.variants;
        prod.suggested_products = payload.suggested_products;
      }
      return prod;
    });
  },
  UPDATE_PROPERTIES(state, payload) {
    state.properties = payload;
    state.properties = state.properties.map((p) => {
      const property = p;
      property.values = [];
      property.selectedValues = [];
      return property;
    });
  },
  UPDATE_PROPERTIES_VALUES(state, values) {
    state.properties = state.properties.map((p) => {
      const property = p;
      property.values = [];
      values.forEach((value) => {
        if (property.id === value.id) {
          property.values.push(value.value);
        }
      });
      property.values.sort();
      return property;
    });
  },
  ADD_PROPERTY_VALUE(state, { propertyId, value }) {
    state.properties.map((p) => {
      const property = p;
      if (property.id === propertyId) {
        property.selectedValues = [...p.selectedValues, value];
      }
      return property;
    });
  },
  DELETE_PROPERTY_VALUE(state, { propertyId, value }) {
    state.properties = state.properties.map((p) => {
      const property = p;
      if (property.id === propertyId) {
        property.selectedValues = p.selectedValues.filter((v) => v !== value);
      }
      return property;
    });
  },
  UPDATE_OPTIONS(state, payload) {
    state.options = payload;
    state.options = state.options.map((o) => {
      const option = o;
      option.values = [];
      option.selectedValues = [];
      return option;
    });
  },
  UPDATE_OPTIONS_VALUES(state, values) {
    state.options = state.options.map((o) => {
      const option = o;
      option.values = [];
      values.forEach((value) => {
        if (option.id === value.option_type_id) {
          option.values.push(value);
        }
      });
      option.selectedValues = option.selectedValues.map(
        (selectedValue) => values.find((realValue) => realValue.id === selectedValue.id),
      );
      return option;
    });
  },
  ADD_OPTION_VALUE(state, { optionId, value }) {
    state.options = state.options.map((o) => {
      const option = o;
      if (option.id === optionId) {
        option.selectedValues = [...o.selectedValues, value];
      }
      return option;
    });
  },
  DELETE_OPTION_VALUE(state, { optionId, value }) {
    state.options = state.options.map((o) => {
      const option = o;
      if (option.id === optionId) {
        option.selectedValues = o.selectedValues.filter((v) => v !== value);
      }
      return option;
    });
  },
  UPDATE_SUPPLIER(state, payload) {
    state.supplier = payload;
  },
  UPDATE_PAGINATION(state, payload) {
    state.pagination = payload;
  },
  UPDATE_PAGINATION_CURRENT(state, page) {
    const p = { current: page };
    state.pagination = { ...state.pagination, ...p };
  },
  PAGINATION_RESET_TOTAL_COUNT(state) {
    const tc = { total_count: null };
    state.pagination = { ...state.pagination, ...tc };
  },
  UPDATE_FILTER_STATUSES(state, payload) {
    state.filterStatuses = payload;
  },
};

const actions = {
  setLocale({ commit }, locale) {
    commit('UPDATE_LOCALE', locale);
  },

  getTaxons({ commit, state }) {
    return axios({
      method: 'get',
      url: routes.allTreeTaxonsIndexUrl,
      params: { set: 'nested', locale: state.locale },
      headers: {
        'content-type': 'application/json',
        'X-CSRF-TOKEN': config.csrfToken,
      },
    }).then((response) => {
      commit('UPDATE_TAXONS', response.data.taxonomies);
    });
  },

  setTaxon({ commit }, taxon) {
    commit('UPDATE_SELECTED_TAXON', taxon);
    commit('UPDATE_PAGINATION_CURRENT', 1);
    // dispatch('getProperties');
    // dispatch('getOptions');
  },

  setTaxonFromPermalink({ dispatch, state }, taxonPermalink) {
    const taxon = taxonPermalink ? findTaxon(state.taxons, taxonPermalink) : null;
    const event = new CustomEvent('search:selectedTaxon', { detail: taxonPermalink });
    window.dispatchEvent(event);
    dispatch('setTaxon', taxon);
  },

  setSupplierFromMainApp({ dispatch, state }) {
    const option = state.options.find((o) => o.name === 'Supplier');
    if (option) {
      const optionValue = option.values.find((ov) => ov.slug === state.supplier);
      if (optionValue) {
        dispatch('addOptionValue', { optionId: option.id, value: optionValue });
      }
    }
  },

  updateCriteria({ commit, dispatch }, criteria) {
    commit('UPDATE_CRITERIA', criteria);
    dispatch('getProducts');
  },

  setQuery({ commit, dispatch }, event) {
    const query = event.target.value;
    commit('UPDATE_QUERY', query);
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      commit('UPDATE_PAGINATION_CURRENT', 1);
      dispatch('getProducts');
    }, 1000);
  },

  getProducts({ commit, state }) {
    commit('UPDATE_PRODUCTS', undefined);
    const taxonId = state.selectedTaxon ? state.selectedTaxon.id : 0;
    return axios({
      method: 'get',
      url: routes.productsIndexUrl,
      params: {
        page: state.pagination.current,
        per_page: state.pagination.per_page,
        query: state.query,
        taxon_id: taxonId === 0 ? '' : taxonId,
        property_values: propertyParams(state.properties),
        option_values: optionParams(state.options),
        locale: state.locale,
      },
      headers: {
        'content-type': 'application/json',
      },
    }).then((response) => {
      commit('UPDATE_PRODUCTS', response.data.products);
      commit('UPDATE_PAGINATION', response.data.pagination);
      commit('UPDATE_FILTER_STATUSES', response.data.aggregations);
    });
  },

  getProduct({ commit }, product) {
    return axios({
      method: 'get',
      url: `${routes.productShowUrl}/${product.id}`,
      params: { locale: state.locale },
      headers: {
        'content-type': 'application/json',
      },
    }).then((response) => {
      commit('UPDATE_PRODUCT', response.data);
    });
  },

  getProperties({ commit, dispatch, state }) {
    return axios({
      method: 'get',
      url: routes.propertiesIndexUrl,
      params: {
        filters: Object.values(state.propertiesFilters).map((f) => f.value), locale: state.locale,
      },
      headers: {
        'content-type': 'application/json',
        'X-CSRF-TOKEN': config.csrfToken,
      },
    }).then((response) => {
      commit('UPDATE_PROPERTIES', response.data.properties);
      dispatch('getPropertiesValues', state.properties);
    });
  },

  getPropertiesValues({ commit }, properties) {
    const propertiesIds = properties.map((p) => p.id);
    const taxonId = selectedTaxonIdParam(state.selectedTaxon);
    return axios({
      method: 'get',
      url: routes.propertiesValuesIndexUrl,
      params: { properties_ids: propertiesIds, taxon_id: taxonId, locale: state.locale },
      headers: {
        'content-type': 'application/json',
        'X-CSRF-TOKEN': config.csrfToken,
      },
    }).then((response) => {
      commit('UPDATE_PROPERTIES_VALUES', response.data.values);
    });
  },

  addPropertyValue({ commit }, { propertyId, value }) {
    commit('ADD_PROPERTY_VALUE', { propertyId, value });
    // commit('UPDATE_PAGINATION_CURRENT', 1);
    // dispatch('getProducts');
  },

  deletePropertyValue({ commit }, { propertyId, value }) {
    commit('DELETE_PROPERTY_VALUE', { propertyId, value });
    // commit('UPDATE_PAGINATION_CURRENT', 1);
    // dispatch('getProducts');
  },

  getOptions({ commit, dispatch, state }) {
    return axios({
      method: 'get',
      url: routes.optionsIndexUrl,
      params: {
        filters: Object.values(
          state.optionsFilters,
        ).map((f) => f.value),
        locale: state.locale,
      },
      headers: {
        'content-type': 'multipart/form-data',
        'X-CSRF-TOKEN': config.csrfToken,
      },
    }).then((response) => {
      commit('UPDATE_OPTIONS', response.data.option_types);
      dispatch('getOptionsValues', state.options);
    });
  },

  getOptionsValues({ commit, dispatch, state }, options) {
    const optionsIds = options.map((o) => o.id);
    const taxonId = selectedTaxonIdParam(state.selectedTaxon);
    return axios({
      method: 'get',
      url: routes.optionsValuesIndexUrl,
      params: { options_ids: optionsIds, taxon_id: taxonId, locale: state.locale },
      headers: {
        'content-type': 'multipart/form-data',
        'X-CSRF-TOKEN': config.csrfToken,
      },
    }).then((response) => {
      commit('UPDATE_OPTIONS_VALUES', response.data.values);
      if (state.supplier) {
        dispatch('setSupplierFromMainApp');
      }
    });
  },

  addOptionValue({ dispatch, commit }, { optionId, value }) {
    commit('ADD_OPTION_VALUE', { optionId, value });
    commit('UPDATE_PAGINATION_CURRENT', 1);
    dispatch('getProducts');
  },

  deleteOptionValue({ dispatch, commit }, { optionId, value }) {
    commit('DELETE_OPTION_VALUE', { optionId, value });
    commit('UPDATE_PAGINATION_CURRENT', 1);
    dispatch('getProducts');
  },

  setPage({ dispatch, commit }, page) {
    commit('UPDATE_PAGINATION_CURRENT', page);
    dispatch('getProducts');
  },
};

const getters = {
  query: (state) => state.query,
  locale: (state) => state.locale,
  products: (state) => state.products,
  taxons: (state) => state.taxons,
  selectedTaxon: (state) => state.selectedTaxon,
  properties: (state) => state.properties,
  options: (state) => state.options,
  pagination: (state) => state.pagination,
  filterStatuses: (state) => {
    if (!state.products) return {};
    const statuses = {};

    state.filterStatuses.forEach((status) => {
      statuses[status.name] = {};
      status.values.forEach((value) => {
        statuses[status.name][value.id] = value.count;
      });
    });

    return statuses;
  },
};

const searchModule = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};

export default searchModule;
